import { ff } from '@atlassian/jira-feature-flagging';
import { fetchJson, performPostRequest } from '@atlassian/jira-fetch';
import type { useIntl } from '@atlassian/jira-intl';
import {
	isSynthetic,
	X2C_SERVICE_URL_PREFIX,
} from '@atlassian/jira-project-create-importer-common';
import type {
	AccessTokenStatus,
	ProjectData,
	StartThirdPartyImportPayload,
	ThirdPartyEntity,
	ThirdPartyEntityPage,
	ThirdPartyEntityStats,
	ThirdPartyEntityStatuses,
	ThirdPartyMembers,
} from '../../../types';
import type { ThirdPartyClient, ThirdPartyFieldConfigData } from '../types';
import { transformToFolderAndSubfolderState } from '../utils';
import { mapItemTypes } from './utils';

interface ClickupClientConstructorParams {
	cloudId: string;
	formatMessage: ReturnType<typeof useIntl>['formatMessage'];
}

export class ClickupClient implements ThirdPartyClient {
	private BASE_URL: string;

	private readonly BASE_HEADERS: Record<string, string> = {
		'x-issynthetic-traffic': String(isSynthetic()),
		'x-migration-authentication-type': 'oauth',
	};

	private readonly formatMessage: ReturnType<typeof useIntl>['formatMessage'];

	constructor({ cloudId, formatMessage }: ClickupClientConstructorParams) {
		this.BASE_URL = `${X2C_SERVICE_URL_PREFIX}/${cloudId}/import/clickup`;
		this.formatMessage = formatMessage;
	}

	async getEntities({
		spaceId,
		folderIds,
	}: {
		spaceId: string;
		folderIds?: string[];
	}): Promise<ThirdPartyEntity[]> {
		let url = `${this.BASE_URL}/space/${spaceId}/lists`;
		if (folderIds) {
			url = `${url}?folderId=${folderIds}`;
		}
		const entities = await fetchJson<ThirdPartyEntity[]>(url, {
			method: 'GET',
			headers: this.BASE_HEADERS,
		});

		return entities;
	}

	getEntitiesPage(): Promise<ThirdPartyEntityPage> {
		return Promise.reject(new Error('getEntitiesPage method is not implemented in Clickup'));
	}

	searchEntities(): Promise<ThirdPartyEntity[]> {
		return Promise.reject(new Error('searchEntities method is not implemented in Clickup'));
	}

	getEntityStats({ entityId }: { entityId: string }): Promise<ThirdPartyEntityStats> {
		const url = `${this.BASE_URL}/list/${entityId}/stats`;

		return fetchJson<ThirdPartyEntityStats>(url, {
			method: 'GET',
			headers: {
				...this.BASE_HEADERS,
			},
		});
	}

	getMultiEntityStats({ entityIds }: { entityIds: string[] }): Promise<ThirdPartyEntityStats> {
		const url = `${this.BASE_URL}/list/stats`;

		return fetchJson<ThirdPartyEntityStats>(url, {
			method: 'POST',
			body: JSON.stringify({
				listIds: entityIds,
			}),
			headers: this.BASE_HEADERS,
		});
	}

	getEntityMembers(): Promise<ThirdPartyMembers[]> {
		return Promise.reject(new Error('getEntityMembers method is not implemented in Clickup'));
	}

	getWorkspaces(): Promise<ThirdPartyEntity[]> {
		const url = `${this.BASE_URL}/workspaces`;

		return fetchJson<ThirdPartyEntity[]>(url, {
			method: 'GET',
			headers: this.BASE_HEADERS,
		});
	}

	startImport({
		entityId,
		parentEntityId,
		isMultiEntity = false,
		payload,
	}: {
		entityId: string;
		isMultiEntity?: boolean;
		parentEntityId: string;
		payload: StartThirdPartyImportPayload;
	}): Promise<{ migrationId: string }> {
		const url = isMultiEntity
			? `${this.BASE_URL}/workspace/${parentEntityId}/list/migration/start`
			: `${this.BASE_URL}/workspace/${parentEntityId}/list/${entityId}/migration/start`;

		return performPostRequest(url, {
			body: JSON.stringify(payload),
			headers: {
				...this.BASE_HEADERS,
				'Content-Type': 'application/json',
			},
		});
	}

	async getFieldConfig({
		entityId,
		entities,
		projectData,
	}: {
		entityId: string;
		entities: ThirdPartyEntity[];
		projectData: ProjectData;
	}): Promise<ThirdPartyFieldConfigData> {
		if (entities.length === 0 && ff('x2c-clickup-importer-multi-list')) {
			return Promise.reject(new Error('No entities provided'));
		}

		const url = ff('x2c-clickup-importer-multi-list')
			? `${this.BASE_URL}/list/fields`
			: `${this.BASE_URL}/list/${entityId}/fields`;

		const data = await fetchJson<ThirdPartyFieldConfigData>(url, {
			method: 'POST',
			body: JSON.stringify({
				projectDetails: {
					type: projectData.type,
					style: projectData.style,
				},
				...(ff('x2c-clickup-importer-multi-list') ? { entities } : {}),
			}),
			headers: this.BASE_HEADERS,
		});

		return {
			...data,
			itemTypes: mapItemTypes(data.itemTypes, this.formatMessage),
		};
	}

	validateAccessToken(): Promise<{ status: AccessTokenStatus }> {
		return Promise.reject(new Error('validateAccessToken method is not implemented in Clickup'));
	}

	postAccessToken(): Promise<void> {
		return Promise.reject(new Error('postAccessToken method is not implemented in Clickup'));
	}

	async getFolders({ entityId }: { entityId: string }) {
		const url = `${this.BASE_URL}/space/${entityId}/folders`;

		const folders = await fetchJson(url, {
			method: 'GET',
			headers: this.BASE_HEADERS,
		});
		const data = transformToFolderAndSubfolderState(folders);
		return data;
	}

	getSiteName(): Promise<string> {
		return Promise.reject(new Error('getSiteName method is not implemented in Clickup'));
	}

	getSpaces({ workspaceId }: { workspaceId: string }): Promise<ThirdPartyEntity[]> {
		const url = `${this.BASE_URL}/workspace/${workspaceId}/spaces`;

		return fetchJson<ThirdPartyEntity[]>(url, {
			method: 'GET',
			headers: this.BASE_HEADERS,
		});
	}

	getMultiEntityStatuses({
		entityIds,
		projectData,
	}: {
		entityIds: string[];
		projectData: ProjectData;
	}): Promise<ThirdPartyEntityStatuses> {
		const url = `${this.BASE_URL}/list/statuses`;

		return fetchJson<ThirdPartyEntityStatuses>(url, {
			method: 'POST',
			body: JSON.stringify({
				projectDetails: {
					type: projectData.type,
					style: projectData.style,
				},
				listIds: entityIds,
			}),
			headers: this.BASE_HEADERS,
		});
	}
}
